import Analytics from '@opencare/analytics';
import { includes } from 'lodash';

import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getInsuranceList,
  getInsuranceListForCanada,
  requestEligiblePartners,
} from 'connect/actions';
import { UserMarketName } from 'connect/saga/handlers/user/user-market-name';
import {
  selectDataEligiblePartners,
  selectDataLoadingEligiblePartners,
} from 'connect/selectors';
import { AimOutlined, LoadingOutlined } from 'opencare-crown';

import { useUpdateLocationInStorage } from 'pages/funnel/storage/hooks';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useOfferService } from 'services/OfferService';
import { useRedirectionService } from 'services/RedirectionService';
import loadScript from 'utils/loadScript';
import Logger from 'utils/Logger';

import { COUNTRY, SPECIAL_CONFIG_IDS } from '../../../../common/constants';
import { CONCIERGE_PATH } from '../../../../containers/App/Routes';
import {
  GOOGLE_MAPS_URL,
  GOOGLE_SCRIPT_ID,
} from '../../../common/constants/google-maps-constants';
import {
  AdditionalBlock,
  PrimaryButton,
  Subtitle,
  Title,
} from '../../../common/styled-components';
import { selectAndSavePartner } from '../../../concierge/helpers';
import { useFunnelStorage } from '../../storage/Storage';
import {
  ButtonBlock,
  StepWrapper,
  TertiaryButton,
} from '../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../utils';

import {
  CurrentLocationWrapper,
  Input,
  InputWrapper,
  Label,
  LoadingPartners,
  StyledAlert,
} from './styled-components';

const analytics = new Analytics();

const Location = ({ position, moveToNextStep }) => {
  const dispatch = useDispatch();
  const storage = useFunnelStorage();

  const offerService = useOfferService();
  const analyticsService = useAnalyticsService();
  const redirectionService = useRedirectionService();

  let autocomplete;
  const input = useRef();

  const savedLocation = storage.get(STEP_KEY.LOCATION);

  const [value, setValue] = useState(
    savedLocation ? savedLocation.formattedAddress : '',
  );
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [inputFocus, setInputFocus] = useState(false);
  const [geolocateLoading, setGeolocateLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const loadingPartners = useSelector(selectDataLoadingEligiblePartners);
  const eligiblePartners = useSelector(selectDataEligiblePartners);

  const userMarketName = useSelector(UserMarketName.value.selector);

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_location',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });
  }, []);

  useEffect(() => {
    if (!showLoading && !loadingPartners) {
      loadScript(GOOGLE_MAPS_URL, GOOGLE_SCRIPT_ID, setupAutocomplete);
    }

    if (!showLoading || loadingPartners) {
      return;
    }

    if (!eligiblePartners?.length) {
      return;
    }

    selectAndSavePartner(eligiblePartners, storage);
    setTimeout(() => {
      redirectionService.redirect({
        url: CONCIERGE_PATH,
        reason: 'no-eligible-partners',
      });
    }, 100);
  }, [showLoading, loadingPartners]);

  const setupAutocomplete = () => {
    if (!input.current) {
      return;
    }

    autocomplete = new window.google.maps.places.Autocomplete(input.current, {
      types: ['geocode'],
    });
    autocomplete.setFields([
      'address_component',
      'formatted_address',
      'geometry',
    ]);
    autocomplete.setComponentRestrictions({
      country: ['us', 'ca'],
    });

    autocomplete.addListener('place_changed', handlePlaceSelect);
    addAutoselectListener();
  };

  // Many thanks to stackoverflow
  // https://stackoverflow.com/questions/7865446/google-maps-places-api-v3-autocomplete-select-first-option-on-enter
  // TLDR; this method adds an event listener. If user types an address but
  // does NOT explicitly select an option from Google when they click "enter"
  // this will simulate a down arrow event in order to select the first option from Google
  const addAutoselectListener = () => {
    if (!input.current) {
      return;
    }

    const addEventListener = input.current.addEventListener
      ? input.current.addEventListener
      : input.attachEvent;
    const addEventListenerWrapper = (type, listener) => {
      let editableListener = listener;

      if (type === 'keydown') {
        const originalListener = listener;

        editableListener = (event) => {
          // Check if a suggestion from Google has already been selected
          const selectedOption = document.querySelector('.pac-item-selected');

          // if enter and no option selected, simulate down arrow
          if (event.key === 'Enter' && !selectedOption) {
            const downArrow = new KeyboardEvent('keydown', {
              keyCode: 40,
              key: 'ArrowDown',
            });
            originalListener.apply(input.current, [downArrow]);
          }

          originalListener.apply(input.current, [event]);
        };
      }

      addEventListener.apply(input.current, [type, editableListener]);
    };

    input.current.addEventListener = addEventListenerWrapper;
    input.current.attachEvent = addEventListenerWrapper;
  };

  const parsePlace = (place) => {

    if (!place?.formatted_address) {
      return;
    }

    const address = {
      address: {},
      formattedAddress: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
    };

    // Transform address components into a single object
    for (let i = 0; i < place.address_components.length; i += 1) {
      const component = place.address_components[i];

      address.address[component.types[0]] = component.long_name;

      if (component.types[0] === 'administrative_area_level_1') {
        address.address[`${component.types[0]}_short`] = component.short_name;
      }
    }

    // Edge cases
    // Sometimes geocoder response doesn't contain `locality` attribute that is required for API.
    // For example: http://maps.googleapis.com/maps/api/geocode/json?address=6414%20Bay%20Pkwy,%20New%20York,%20United%20States,%2011204
    // In this case use value of `sublocality_level_1` or `sublocality` attribute.
    if (
      !address.address.locality
      && address.address.route
      && address.address.administrative_area_level_1
    ) {
      address.address.locality =
        address.address.sublocality_level_1 || address.address.sublocality;
    }

    // Remove _prefix from postal_code
    if (!address.address.postal_code && address.address.postal_code_prefix) {
      address.address.postal_code = address.address.postal_code_prefix;
      delete address.address.postal_code_prefix;
    }

    setValue(place.formatted_address);
    setResult(address);
    setError(null);
  };

  const handlePlaceSelect = () => {
    const place = autocomplete.getPlace();

    parsePlace(place);
  };

  const handleChange = (e) => {
    // In order to prevent accidentally sending their previous result,
    // if user starts typing again we'll clear their previous result
    if (result && e.target.value) {
      setResult(null);
    }

    setValue(e.target.value);
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!result?.address?.locality) {
      setError('Please enter a more precise location.');
      return;
    }

    setHasSubmitted(true);
    setShowLoading(true);
  };

  const locationUpdateState = useUpdateLocationInStorage({
    newLocation: result,
    shouldUpdate: hasSubmitted,
  });

  useEffect(() => {
    if (
      !hasSubmitted
      || !result
      || !locationUpdateState.isFinished
      || !userMarketName
    ) {
      return;
    }

    analytics.trackForm({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_location',
      userSuppliedLocation: {
        country: result.address.country,
        state: result.address.administrative_area_level_1_short,
        city: result.address.locality,
        street:
          result.address.street_number && result.address.route
            ? `${result.address.street_number} ${result.address.route}`
            : null,
        postal: result.address.postal_code ? result.address.postal_code : null,
        market: userMarketName,
      },
      ...generateStepAnalytics(position, storage),
    });

    dispatch(requestEligiblePartners(result));
    dispatch(
      getInsuranceList(result.address.administrative_area_level_1_short),
    );

    if (result.address.country === COUNTRY.canada) {
      dispatch(getInsuranceListForCanada());
    }

    // Resetting funnel config triggers new config assignment
    if (storage.config?.id === SPECIAL_CONFIG_IDS.PREFUNNEL) {
      moveToNextStep({ reset: true });
    }

    if (offerService.hasActiveOffer) {
      moveToNextStep();
      return;
    }

    moveToNextStep();
  }, [hasSubmitted, userMarketName, result, locationUpdateState]);

  const geolocate = () => {
    setGeolocateLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async ({ coords }) => {
        const address = `${coords.latitude},${coords.longitude}`;

        const geocoder = geocoder || new window.google.maps.Geocoder();

        const geocodedLocation = await new Promise((resolve) => {
          geocoder.geocode({ address }, (results, status) => {
            resolve({ results, status });
          });
        });

        if (geocodedLocation.status === 'OK') {
          parsePlace(geocodedLocation.results[0]);
        } else {
          // Tracked statuses: 'OVER_QUERY_LIMIT', 'REQUEST_DENIED', 'INVALID_REQUEST', 'UNKNOWN_ERROR', 'ERROR', 'NOT_FOUND'
          const ignoredStatuses = ['ZERO_RESULTS'];
          const warningStatuses = ['UNKNOWN_ERROR'];
          if (includes(warningStatuses, geocodedLocation.status)) {
            Logger.warning(`Google Geocode: ${geocodedLocation.status}`, {
              address,
            });
          }

          if (!includes(ignoredStatuses, geocodedLocation.status)) {
            Logger.error(`Google Geocode: ${geocodedLocation.status}`, {
              address,
            });
          }
        }

        setGeolocateLoading(false);
      });
    } else {
      setError('Geolocation is not supported by this browser.');
      setGeolocateLoading(false);
    }
  };

  if (showLoading || loadingPartners) {
    return (
      <LoadingPartners>
        <LoadingOutlined />
        <p>Loading</p>
      </LoadingPartners>
    );
  }

  return (
    <div>
      <Title>Let's find you a top-rated dentist.</Title>
      <Subtitle>
        Enter your home or work address and we'll find you a great dentist
        nearby.
      </Subtitle>
      <StepWrapper>
        <form onSubmit={onSubmit}>
          <AdditionalBlock>
            <InputWrapper>
              <Label htmlFor="location" hasText={value !== '' || inputFocus}>
                Enter a location
              </Label>
              <Input
                type="text"
                ref={input}
                id="location"
                value={value}
                onChange={handleChange}
                onBlur={() => setInputFocus(false)}
                onFocus={() => setInputFocus(true)}
                data-testid="location-input"
              />
            </InputWrapper>
            {error
            && <StyledAlert type="error" data-testid="error" message={error} />}
            <CurrentLocationWrapper>
              <TertiaryButton
                type="button"
                onClick={geolocate}
                data-testid="location-geolocate"
              >
                {geolocateLoading ? <LoadingOutlined /> : <AimOutlined />}
                Use my current location
              </TertiaryButton>
            </CurrentLocationWrapper>
          </AdditionalBlock>
          <ButtonBlock>
            <PrimaryButton
              data-testid="location-submit"
              type="submit"
              disabled={!result}
            >
              Continue
            </PrimaryButton>
          </ButtonBlock>
        </form>
      </StepWrapper>
    </div>
  );
};

Location.defaultProps = {
  position: '',
};

Location.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
};

export default Location;
